import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ArrowLeft, Clock, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import SubscriptionStatus from "@/components/subscription/SubscriptionStatus";
import BillingCycleToggle from "@/components/subscription/BillingCycleToggle";
import PlanCard from "@/components/subscription/PlanCard";
import AddOnSection from "@/components/subscription/AddOnSection";
import SubscriptionConfirmDialog from "@/components/subscription/SubscriptionConfirmDialog";
import { plans, addOns, ANNUAL_DISCOUNT } from "@/components/subscription/plans-data";
import PerChildCard, { PRICE_PER_CHILD, MIN_CHARGE } from "@/components/subscription/PerChildCard";
import BranchBillingBreakdown from "@/components/subscription/BranchBillingBreakdown";

const Subscription = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [perChildMode, setPerChildMode] = useState(false);

  // Fetch active children count
  const { data: childrenCount = 0 } = useQuery({
    queryKey: ["children-count", profile?.nursery_id],
    queryFn: async () => {
      if (!profile?.nursery_id) return 0;
      const { count } = await supabase
        .from("children")
        .select("*", { count: "exact", head: true })
        .eq("nursery_id", profile.nursery_id);
      return count || 0;
    },
    enabled: !!profile?.nursery_id,
  });

  // Fetch branch billing breakdown
  const { data: branchBreakdown } = useQuery({
    queryKey: ["branch-breakdown", profile?.nursery_id],
    queryFn: async () => {
      if (!profile?.nursery_id) return null;
      const { data } = await supabase.rpc("get_branch_billing_breakdown", {
        _parent_nursery_id: profile.nursery_id,
      });
      return data as any;
    },
    enabled: !!profile?.nursery_id,
  });

  const { data: currentSub, isLoading } = useQuery({
    queryKey: ["my-subscription", profile?.nursery_id],
    queryFn: async () => {
      if (!profile?.nursery_id) return null;
      const { data } = await supabase
        .from("nursery_subscriptions")
        .select("*")
        .eq("nursery_id", profile.nursery_id)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();
      return data;
    },
    enabled: !!profile?.nursery_id,
  });

  // Fetch pending subscription requests
  const { data: pendingRequests } = useQuery({
    queryKey: ["my-subscription-requests", profile?.nursery_id],
    queryFn: async () => {
      if (!profile?.nursery_id) return [];
      const { data } = await supabase
        .from("subscription_requests")
        .select("*")
        .eq("nursery_id", profile.nursery_id)
        .order("created_at", { ascending: false })
        .limit(10);
      return (data || []) as any[];
    },
    enabled: !!profile?.nursery_id,
  });

  const isTrial = (currentSub as any)?.is_trial;
  const trialEndsAt = (currentSub as any)?.trial_ends_at;

  const daysRemaining = useMemo(() => {
    const endDate = isTrial ? trialEndsAt : currentSub?.expires_at;
    if (!endDate) return null;
    const diff = new Date(endDate).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [currentSub, isTrial, trialEndsAt]);

  const getPrice = (monthlyPrice: number) => {
    if (billingCycle === "yearly") {
      return Math.round(monthlyPrice * 12 * (1 - ANNUAL_DISCOUNT / 100));
    }
    return monthlyPrice;
  };

  const handleSelectPlan = (planId: string) => {
    if (currentSub?.plan === planId && currentSub?.status === "active" && !isTrial) return;
    setSelectedPlan(planId);
    setPerChildMode(false);
    if (planId === "standard" || planId === "premium") {
      setSelectedAddOns((prev) => prev.filter((a) => a !== "educational"));
    }
    setConfirmOpen(true);
  };

  const handleSelectPerChild = () => {
    setSelectedPlan("per_child");
    setPerChildMode(true);
    setConfirmOpen(true);
  };

  const toggleAddOn = (addOnId: string) => {
    setSelectedAddOns((prev) =>
      prev.includes(addOnId) ? prev.filter((a) => a !== addOnId) : [...prev, addOnId]
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-6xl mx-auto" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">الاشتراك والخطط</h1>
          <p className="text-muted-foreground text-sm">اختر النظام المناسب لحضانتك — ابدأ بالإداري وأضف ما تحتاجه</p>
        </div>
        <Button variant="ghost" onClick={() => navigate("/dashboard")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          العودة
        </Button>
      </div>

      {/* Current Subscription Status */}
      <SubscriptionStatus
        currentSub={currentSub}
        isTrial={isTrial}
        daysRemaining={daysRemaining}
        plans={plans}
      />

      {/* Pending Requests */}
      {pendingRequests && pendingRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5" />
              طلبات الاشتراك
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingRequests.map((req: any) => (
              <div key={req.id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {req.request_type === "new" ? "اشتراك جديد" : req.request_type === "upgrade" ? "ترقية" : "تجديد"}
                    </span>
                    <span className="text-sm">→ خطة {plans.find(p => p.id === req.requested_plan)?.name || req.requested_plan}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {format(new Date(req.created_at), "dd/MM/yyyy HH:mm")} · {req.amount?.toLocaleString()} ج.م · {req.billing_cycle === "yearly" ? "سنوي" : "شهري"}
                  </div>
                  {req.admin_notes && (
                    <p className="text-xs text-muted-foreground">ملاحظات المشرف: {req.admin_notes}</p>
                  )}
                </div>
                <Badge variant={
                  req.status === "pending" ? "secondary" :
                  req.status === "approved" ? "default" : "destructive"
                } className="flex items-center gap-1">
                  {req.status === "pending" && <Clock className="w-3 h-3" />}
                  {req.status === "approved" && <CheckCircle className="w-3 h-3" />}
                  {req.status === "rejected" && <XCircle className="w-3 h-3" />}
                  {req.status === "pending" ? "قيد المراجعة" : req.status === "approved" ? "تمت الموافقة" : "مرفوض"}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <BillingCycleToggle
        billingCycle={billingCycle}
        setBillingCycle={setBillingCycle}
        discount={ANNUAL_DISCOUNT}
      />

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            price={getPrice(plan.monthlyPrice)}
            billingCycle={billingCycle}
            isCurrentPlan={currentSub?.plan === plan.id && currentSub?.status === "active" && !isTrial}
            isTrial={isTrial}
            hasSubscription={!!currentSub}
            onSelect={handleSelectPlan}
          />
        ))}
      </div>

      {/* Per-Child Option */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <PerChildCard
            childrenCount={childrenCount}
            isCurrentModel={currentSub?.pricing_model === "per_child" && currentSub?.status === "active"}
            onSelect={handleSelectPerChild}
          />
        </div>
      </div>

      {/* Branch Billing Breakdown */}
      {branchBreakdown?.branches && branchBreakdown.branches.length > 1 && (
        <BranchBillingBreakdown
          branches={branchBreakdown.branches}
          grandTotal={branchBreakdown.grand_total}
          totalChildren={branchBreakdown.total_children}
          minChargeApplied={branchBreakdown.min_charge_applied}
          minCharge={branchBreakdown.min_charge}
        />
      )}

      {/* Add-ons Section */}
      <AddOnSection
        addOns={addOns}
        selectedAddOns={selectedAddOns}
        toggleAddOn={toggleAddOn}
        currentPlan={currentSub?.plan}
        billingCycle={billingCycle}
        getPrice={getPrice}
      />

      {/* Confirm Dialog */}
      <SubscriptionConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        selectedPlan={selectedPlan}
        selectedAddOns={perChildMode ? [] : selectedAddOns}
        billingCycle={billingCycle}
        getPrice={getPrice}
        plans={plans}
        addOns={addOns}
        currentSub={currentSub}
        isTrial={isTrial}
        nurseryId={profile?.nursery_id}
        perChildMode={perChildMode}
        childrenCount={childrenCount}
      />
    </div>
  );
};

export default Subscription;
